import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui-bits";
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Euro, Briefcase, MapPin, Users as UsersIcon, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT_ES = ["L","M","X","J","V","S","D"];
const DAYS_SHORT_EN = ["M","T","W","T","F","S","S"];

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

export default function MisHoras() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const today = new Date();
  const initMonth = sp.get("month") || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(initMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const empleadoQ = sp.get("empleado_id");
  const [allEmpleados, setAllEmpleados] = useState([]);
  const [selEmp, setSelEmp] = useState(empleadoQ || "");
  const [embs, setEmbs] = useState([]);
  const [servs, setServs] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/users").then((r) => setAllEmpleados(r.data.filter((x) => x.role === "empleado")));
    }
    Promise.all([api.get("/embarcaciones"), api.get("/servicios")]).then(([e, s]) => {
      setEmbs(e.data); setServs(s.data);
    });
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const params = { month };
    if (user?.role === "admin" && selEmp) params.empleado_id = selEmp;
    api.get("/informes/horas-empleado", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [month, selEmp, user]);

  const embMap = useMemo(() => Object.fromEntries(embs.map((x) => [x.id, x])), [embs]);
  const servMap = useMemo(() => Object.fromEntries(servs.map((x) => [x.id, x])), [servs]);

  const changeMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setMonth(newMonth);
    setSp({ month: newMonth, ...(selEmp && { empleado_id: selEmp }) });
  };

  const daysByDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [data]);

  const calendar = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const startOffset = (first.getDay() + 6) % 7;
    const totalDays = last.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push({ empty: true, key: `e${i}` });
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      const info = daysByDate[iso];
      cells.push({
        day: d,
        iso,
        hours: info?.hours || 0,
        services: info?.services || 0,
        isToday: iso === today.toISOString().slice(0, 10),
      });
    }
    return cells;
  }, [month, daysByDate]);

  const selectedDayData = selectedDay && daysByDate[selectedDay];

  return (
    <div className="px-5 pt-8 pb-32 sns-fade-up max-w-6xl mx-auto" data-testid="page-mis-horas">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center" data-testid="btn-back-horas">
          <ArrowLeft size={18} />
        </button>
        <h1 className="sns-title text-2xl flex-1">
          {user?.role === "admin" ? t("horas.title.admin") : t("horas.title.empleado")}
        </h1>
      </div>

      {user?.role === "admin" && (
        <div className="sns-card p-4 mb-4">
          <label className="block text-xs font-semibold text-slate-600 mb-2">{t("horas.employee")}</label>
          <select value={selEmp} onChange={(e) => setSelEmp(e.target.value)} className="sns-input" data-testid="select-empleado-informe">
            <option value="">{t("horas.choose_employee")}</option>
            {allEmpleados.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}

      <div className="sns-card p-4 mb-4 flex items-center justify-between">
        <button onClick={() => changeMonth(-1)} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="btn-prev-month"><ChevronLeft size={18} /></button>
        <div className="text-center">
          <div className="sns-title text-lg capitalize">
            {(lang === "en" ? MONTHS_EN : MONTHS_ES)[parseInt(month.split("-")[1], 10) - 1]} {month.split("-")[0]}
          </div>
          {data?.nombre && <div className="text-xs text-slate-500">{data.nombre}</div>}
        </div>
        <button onClick={() => changeMonth(1)} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="btn-next-month"><ChevronRight size={18} /></button>
      </div>

      {loading ? <Spinner /> : (
        <>
          <div className={`grid ${user?.role === "admin" ? "grid-cols-3" : "grid-cols-2"} gap-3 mb-5`}>
            <SumCard Icon={Clock} label={t("horas.summary.hours")} value={`${data?.total_hours || 0}`} unit="h" color="#FF6B3D" bg="#FFE6D7" />
            <SumCard Icon={Briefcase} label={t("horas.summary.services")} value={`${data?.total_services || 0}`} color="#2D62B7" bg="#E8F1FF" />
            {user?.role === "admin" && <SumCard Icon={Euro} label={t("horas.summary.total")} value={`${data?.total_euros || 0}`} unit="€" color="#116A34" bg="#D5F5E0" />}
          </div>

          <div className="sns-card p-4 mb-5">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {(lang === "en" ? DAYS_SHORT_EN : DAYS_SHORT_ES).map((d, i) => <div key={i} className="text-center text-xs font-bold text-slate-400 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendar.map((c) => (
                c.empty ? <div key={c.key} /> : (
                  <button
                    key={c.iso}
                    onClick={() => c.hours > 0 && setSelectedDay(c.iso)}
                    disabled={c.hours === 0}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center text-center border transition-all ${
                      c.hours > 0
                        ? "bg-[var(--coral)] text-white border-[var(--coral)] font-bold shadow-sm hover:scale-105 cursor-pointer"
                        : c.isToday
                        ? "bg-[var(--sky)] text-[var(--sky-text)] border-[var(--sky)] font-bold"
                        : "bg-white text-slate-400 border-[var(--border)] cursor-default"
                    }`}
                    data-testid={`day-${c.iso}`}
                  >
                    <div className="text-sm">{c.day}</div>
                    {c.hours > 0 && <div className="text-[10px] opacity-90">{c.hours}h</div>}
                  </button>
                )
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">{t("horas.click_day_hint")}</p>
          </div>

          {data?.days?.length > 0 && !selectedDay && (
            <div className="space-y-3">
              <h3 className="sns-title text-base px-2">{t("horas.daily_summary")}</h3>
              {data.days.map((d) => (
                <button key={d.date} onClick={() => setSelectedDay(d.date)} className="sns-card p-4 w-full text-left hover:shadow-md transition-shadow" data-testid={`day-summary-${d.date}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold">{formatDateES(d.date)}</div>
                    <div className="sns-pill bg-[var(--coral)] text-white">{d.hours}h</div>
                  </div>
                  <div className="text-xs text-slate-500">{d.services} {d.services !== 1 ? t("horas.service_count_many") : t("horas.service_count_one")} · {t("horas.click_day_hint").split(".")[0]}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Day detail modal */}
      {selectedDayData && (
        <DayDetailModal
          day={selectedDayData}
          onClose={() => setSelectedDay(null)}
          embMap={embMap}
          servMap={servMap}
          navToTrabajo={(id) => nav(`/trabajos/${id}`)}
          t={t}
        />
      )}
    </div>
  );
}

const SumCard = ({ Icon, label, value, unit, color, bg }) => (
  <div className="sns-card p-3 text-center">
    <div className="w-9 h-9 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: bg, color }}>
      <Icon size={16} />
    </div>
    <div className="sns-title text-xl" style={{ color }}>
      {value}{unit && <span className="text-sm ml-0.5">{unit}</span>}
    </div>
    <div className="text-[11px] text-slate-500 font-semibold">{label}</div>
  </div>
);

function DayDetailModal({ day, onClose, embMap, servMap, navToTrabajo, t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.all(
          (day.items || []).map((it) => api.get(`/mantenimientos/${it.id}`).then((r) => r.data).catch(() => null))
        );
        setItems(results.filter(Boolean));
      } finally { setLoading(false); }
    })();
  }, [day]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-detail-modal">
        <div className="sticky top-0 bg-white border-b border-[var(--border)] px-5 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="sns-title text-xl">{formatDateES(day.date)}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{day.hours}h · {day.services} {day.services !== 1 ? t("horas.service_count_many") : t("horas.service_count_one")}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="btn-close-day"><X size={18} /></button>
        </div>

        {loading ? <div className="p-8"><Spinner /></div> : (
          <div className="p-5 space-y-4">
            {items.map((m) => {
              const emb = embMap[m.embarcacion_id];
              const serviciosRealizados = (m.services || []).filter((s) => s.realizado);
              return (
                <div key={m.id} className="sns-card p-4 hover:shadow-md transition-shadow" data-testid={`day-job-${m.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    {emb?.photo_url && <img src={emb.photo_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="sns-title text-lg truncate">{emb?.name || "—"}</h4>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={12} />{m.end_at ? `${fmtTime(m.start_at)} - ${fmtTime(m.end_at)}` : fmtTime(m.start_at)} · {m.hours_worked}h</span>
                        {emb?.puerto && <span className="flex items-center gap-1"><MapPin size={12} />{emb.puerto}</span>}
                      </div>
                    </div>
                  </div>

                  {serviciosRealizados.length > 0 ? (
                    <div className="mb-3">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{t("horas.services_done")}</div>
                      <div className="space-y-1">
                        {serviciosRealizados.map((s) => {
                          const sv = servMap[s.service_id];
                          if (!sv) return null;
                          return (
                            <div key={s.service_id} className="flex items-center gap-2 text-sm">
                              <span className="text-green-600">✓</span>
                              <span className="font-medium">{sv.name}</span>
                              {sv.price > 0 && <span className="text-slate-400 text-xs ml-auto">{sv.price}€</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">{t("horas.no_services")}</p>
                  )}

                  {m.observaciones && (
                    <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-700 mb-3">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t("horas.observations")}</div>
                      {m.observaciones}
                    </div>
                  )}

                  {m.photos?.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto no-scrollbar mb-3">
                      {m.photos.map((p, i) => {
                        const url = typeof p === "string" ? p : p.url;
                        return <img key={i} src={url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />;
                      })}
                    </div>
                  )}

                  <button onClick={() => navToTrabajo(m.id)} className="text-xs font-bold text-[var(--coral)] hover:underline" data-testid={`btn-goto-${m.id}`}>
                    {t("horas.see_full")}
                  </button>
                </div>
              );
            })}
            {items.length === 0 && <p className="text-center text-slate-400 py-4">{t("horas.no_items_day")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

const formatDateES = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
